// Stillhouse — app shell: top bar, side nav router, loader gate, mount.
// Ported verbatim from the Stillhouse design system app shell.
// Auth-gated: ConveyorLoader while the session bootstraps, LoginView when
// unauthenticated, and nav items hidden when the user lacks the permission.
import React, { useEffect, useState } from "react";
import { C, F, Pill, Dot, Btn } from "./brand/apo";
import { StillhouseLogo, StillhouseIcon, ConveyorLoader, Avatar } from "./brand/sh";
import { AuthProvider, useAuth } from "./auth";
import { api } from "./api";
import { DegradedBanner } from "./components/DegradedBanner";
import { LoginView } from "./views/Login";
import { Overview } from "./views/Overview";
import { MachinesPools } from "./views/MachinesPools";
import { Sessions } from "./views/Sessions";
import { TeamsUsers } from "./views/TeamsUsers";
import { Roles } from "./views/Roles";
import { AccessIdentity } from "./views/AccessIdentity";
import { MeshAccess } from "./views/MeshAccess";
import { Security } from "./views/Security";
import { Reports } from "./views/Reports";
import { Performance } from "./views/Performance";
import { Billing } from "./views/Billing";
import { Updates } from "./views/Updates";

type NavItem = [id: string, label: string, Comp: React.ComponentType, perm?: string];
type NavGroup = [title: string, items: NavItem[]];

const NAV: NavGroup[] = [
  ["OVERVIEW", [["dashboard", "Dashboard", Overview]]],
  [
    "FLEET",
    [
      ["machines", "Machines & pools", MachinesPools, "fleet.read"],
      ["sessions", "Sessions", Sessions, "fleet.read"],
    ],
  ],
  [
    "PEOPLE",
    [
      ["teams", "Teams & users", TeamsUsers],
      ["roles", "Roles & permissions", Roles],
    ],
  ],
  [
    "GOVERNANCE",
    [
      ["mesh", "Mesh & access", MeshAccess, "fleet.read"],
      ["access", "Access & identity", AccessIdentity, "settings.manage"],
      ["security", "Security & compliance", Security],
    ],
  ],
  [
    "ACCOUNT",
    [
      ["reports", "Usage & analytics", Reports],
      ["performance", "Performance", Performance],
      ["billing", "Billing & licensing", Billing],
    ],
  ],
  ["SETTINGS", [["updates", "Updates", Updates, "settings.manage"]]],
];

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Account menu"
        style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center" }}
      >
        <Avatar name={user.displayName} size={32} />
      </button>
      {open && (
        <React.Fragment>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 800 }} />
          <div style={{ position: "absolute", top: 42, right: 0, zIndex: 810, width: 250, background: C.surface, border: `1px solid ${C.lineHi}`, boxShadow: "0 18px 50px rgba(0,0,0,0.5)" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.line}`, display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar name={user.displayName} size={34} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: F.ui, fontSize: 13.5, fontWeight: 600, color: C.fgDark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {user.displayName}
                </div>
                <div style={{ fontFamily: F.mono, fontSize: 10.5, color: C.fgFaint, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {user.email}
                </div>
              </div>
            </div>
            <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${C.line}` }}>
              <span style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: "0.18em", color: C.fgFaint, fontWeight: 600 }}>ROLE</span>
              <Pill color={C.lilac} size="sm">{user.role.kind}</Pill>
            </div>
            <div style={{ padding: "12px 16px" }}>
              <Btn
                ghost
                style={{ width: "100%" }}
                onClick={() => {
                  setOpen(false);
                  void logout();
                }}
              >
                Sign out
              </Btn>
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

// Live fleet summary for the top bar: real live-session count and online/total
// machine counts derived from the same endpoints the fleet views fetch. Gated
// on fleet.read so users without it don't trip a 401 (which would log them out).
function FleetSummary() {
  const { can } = useAuth();
  const [summary, setSummary] = useState<{ live: number; online: number; total: number } | null>(null);

  useEffect(() => {
    if (!can("fleet.read")) return;
    let alive = true;
    Promise.all([api.getMachines(), api.getSessions()])
      .then(([m, s]) => {
        if (!alive) return;
        const online = m.machines.filter((x) => x.status === "ONLINE" || x.status === "IN_USE").length;
        setSummary({ live: s.live.length, online, total: m.total });
      })
      .catch(() => {
        /* leave the pill hidden on error */
      });
    return () => {
      alive = false;
    };
  }, [can]);

  if (!summary) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.glowSoft, padding: "7px 14px", border: `1px solid ${C.glow}44`, fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: C.glow, letterSpacing: "0.14em" }}>
      <Dot color={C.glow} size={6} />
      <span>{summary.live} LIVE</span>
      <span style={{ opacity: 0.5 }}>·</span>
      <span>{summary.online}/{summary.total} ONLINE</span>
    </div>
  );
}

function TopBar() {
  return (
    <div style={{ height: 56, background: C.inkHi, borderBottom: `1px solid ${C.line}`, display: "flex", alignItems: "center", gap: 16, padding: "0 22px", flexShrink: 0 }}>
      <StillhouseLogo wordmarkSize={20} iconSize={26} color={C.fgDark} />
      <span style={{ width: 1, height: 18, background: C.line, marginLeft: 4 }} />
      <span style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: "0.26em", color: C.fgFaint, fontWeight: 600 }}>CONTROL PLANE</span>
      <Pill color={C.glow} size="sm">v0.1.1</Pill>
      <div style={{ flex: 1 }} />
      <FleetSummary />
      <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.surface, padding: "7px 12px", border: `1px solid ${C.line}`, fontFamily: F.mono, fontSize: 12, color: C.fgFaint, minWidth: 230 }}>
        <span style={{ opacity: 0.6 }}>⌕</span>
        <span style={{ flex: 1 }}>Search machines, people…</span>
        <span style={{ fontSize: 10 }}>⌘K</span>
      </div>
      <UserMenu />
    </div>
  );
}

function SideNav({ nav, active, onNav }: { nav: NavGroup[]; active: string; onNav: (id: string) => void }) {
  const { user } = useAuth();
  const org = user?.org;
  // Plan, then region only when the org actually has one (Complice IT has none).
  const meta = [org?.plan, org?.region].filter(Boolean).join(" · ").toUpperCase();
  return (
    <div style={{ width: 248, background: C.inkHi, borderRight: `1px solid ${C.line}`, display: "flex", flexDirection: "column", flexShrink: 0, overflow: "auto" }}>
      <div style={{ padding: "20px 22px 16px" }}>
        <div style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: "0.22em", color: C.fgFaint, fontWeight: 600 }}>ORGANISATION</div>
        <div style={{ fontFamily: F.display, fontWeight: 500, fontSize: 16, marginTop: 5, color: C.fgDark, letterSpacing: "-0.01em" }}>{org?.name ?? "—"}</div>
        {meta && <div style={{ fontFamily: F.mono, fontSize: 10, color: C.fgFaint, marginTop: 4, letterSpacing: "0.1em" }}>{meta}</div>}
      </div>
      {nav.map(([title, items]) => (
        <div key={title} style={{ marginTop: 10 }}>
          <div style={{ padding: "0 22px 6px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.24em", color: C.fgFaint, fontWeight: 600 }}>{title}</div>
          {items.map(([id, label]) => {
            const on = active === id;
            return (
              <div
                key={id}
                onClick={() => onNav(id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "9px 22px",
                  cursor: "pointer",
                  fontFamily: F.ui,
                  fontSize: 13.5,
                  fontWeight: on ? 600 : 500,
                  color: on ? C.ink : C.fgDark,
                  background: on ? C.glow : "transparent",
                  borderLeft: `2px solid ${on ? C.glow : "transparent"}`,
                }}
                onMouseEnter={(e) => {
                  if (!on) e.currentTarget.style.background = C.surface;
                }}
                onMouseLeave={(e) => {
                  if (!on) e.currentTarget.style.background = "transparent";
                }}
              >
                {label}
              </div>
            );
          })}
        </div>
      ))}
      <div style={{ flex: 1, minHeight: 20 }} />
      <div style={{ padding: "14px 22px", borderTop: `1px solid ${C.line}`, fontFamily: F.mono, fontSize: 10, color: C.fgFaint, letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 10 }}>
        <StillhouseIcon size={26} color={C.glow} ink={C.fgFaint} />
        <div>
          <div style={{ color: C.fgMute }}>CONTROLLING</div>
          <div style={{ marginTop: 2 }}>MOONSHINE · 0.9.4</div>
        </div>
      </div>
    </div>
  );
}

function Shell() {
  const { can } = useAuth();
  const [view, setView] = useState("dashboard");

  // Hide nav items the user lacks the permission for; drop emptied groups.
  const nav: NavGroup[] = NAV.map(
    ([title, items]) => [title, items.filter(([, , , perm]) => !perm || can(perm))] as NavGroup,
  ).filter(([, items]) => items.length > 0);

  const visible = nav.flatMap(([, items]) => items);
  const entry = visible.find(([id]) => id === view) ?? visible[0];
  const Comp = entry ? entry[2] : null;

  return (
    <div style={{ width: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", background: C.ink, color: C.fgDark, fontFamily: F.ui, overflow: "hidden" }}>
      <TopBar />
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <SideNav nav={nav} active={entry ? entry[0] : view} onNav={setView} />
        <div key={entry ? entry[0] : view} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {Comp ? <Comp /> : <div style={{ padding: 40 }}>Not found</div>}
        </div>
      </div>
    </div>
  );
}

function Gate() {
  const { user, loading } = useAuth();
  const [animDone, setAnimDone] = useState(false);

  // Hold the conveyor until both the animation and the session check finish.
  if (loading || !animDone) {
    return (
      <div style={{ flex: 1, minHeight: 0, background: C.ink }}>
        <ConveyorLoader onDone={() => setAnimDone(true)} />
      </div>
    );
  }
  if (!user) return <LoginView />;
  return <Shell />;
}

export function App() {
  return (
    <AuthProvider>
      {/* The degraded-mode banner sits outside the auth gate on purpose: an
          unreachable database also breaks sign-in, so the login screen needs to
          be able to say so. Everything below it is a flex child, which is why
          Shell/LoginView size with flex rather than 100vh. */}
      <div style={{ width: "100vw", height: "100vh", display: "flex", flexDirection: "column", background: C.ink, overflow: "hidden" }}>
        <DegradedBanner />
        <Gate />
      </div>
    </AuthProvider>
  );
}
